import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  ViewChild,
  OnInit,
  OnDestroy,
} from '@angular/core';
import {
  find,
  findIndex,
  includes,
  values,
  isEqual,
  isFunction,
  isEmpty,
  lte,
  isNil,
} from 'lodash';
import { ICellRendererParams } from 'ag-grid-community';

import { KEY_CODES, NAVIGATION_KEY_CODES } from '@config/global-constants';
import { ISelectOptionsType } from '@shared/types';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services/xvi-translate.service';
import { decimalNumber } from '@app/shared/common';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-custom-select',
  templateUrl: './custom-select.component.html',
  styleUrls: ['./custom-select.component.scss'],
})
export class XVICustomSelectComponent implements OnInit, OnDestroy {
  @ViewChild('selectElement', { static: false })
  public selectElement: HTMLElement;
  @ViewChild('selectBoxElement', { static: false })
  public selectBoxElement: ElementRef;

  @Input() public emptyOption: ISelectOptionsType = null;
  @Input() public options: ISelectOptionsType[];
  @Input() public value: string;
  @Input() public disabled: boolean = false;
  @Input() public isPositionTop: boolean = false;

  /** True when explicitly disabled OR when options list is empty. */
  public get isDisabled(): boolean {
    return this.disabled || isEmpty(this.options);
  }

  @Input() public placeholder: string = null;
  @Input() public error: boolean = false;
  /**
   * Number of items to show in the dropdown before scrolling is needed. Default is 3.
   */
  @Input() public dropdownShownItemCount: number = 3;
  /** When true, a search input appears at the top of the dropdown for fuzzy filtering. */
  @Input() public searchable: boolean = false;
  @Output() public valueChange: EventEmitter<string> =
    new EventEmitter<string>();
  @Output() public focusin: EventEmitter<Event> = new EventEmitter<Event>();
  public isDropdownOpen: boolean = false;
  public highlightedIndex: number = -1;
  public selectedIndex: number = -1;
  public searchText: string = '';
  public filteredOptions: ISelectOptionsType[] = [];
  public readonly I18n = I18n;
  public params: ICellRendererParams;
  public offClickListener: () => void;

  /** Minimum space (px) below the trigger required to open the dropdown downward. */
  private static readonly DROPDOWN_MIN_HEIGHT = 200;
  /** Height of the search input row in px (padding 4×2 + input 28). */
  private static readonly SEARCH_ITEM_HEIGHT = 36;
  /**
   * Computed each time the dropdown opens. True when there is insufficient space
   * below the trigger inside the nearest scroll boundary (AG Grid viewport or window).
   */
  public autoPositionTop: boolean = false;

  /**
   * Calculates the pixel height for displaying exactly `count` dropdown options.
   * Returns `null` when `count` is not positive (caller should use `defaultHeight`).
   */
  private calculateOptionsHeight(count: number): number | null {
    if (lte(count, 0)) {
      return null;
    }

    return decimalNumber(36.67).mul(count).valueOf();
  }

  public get dropdownMaxHeight(): string {
    const defaultHeight = '110px';
    const height = this.calculateOptionsHeight(this.dropdownShownItemCount);

    return height !== null ? `${height}px` : defaultHeight;
  }

  /**
   * Total max height of the ul.dropdown-list.
   * When searchable, adds the search row height on top of the options height
   * so that dropdownShownItemCount items are always fully visible.
   */
  public get dropdownListMaxHeight(): string {
    if (!this.searchable) {
      return this.dropdownMaxHeight;
    }

    const defaultHeight = '110px';
    const height = this.calculateOptionsHeight(this.dropdownShownItemCount);

    return height !== null
      ? `${height + XVICustomSelectComponent.SEARCH_ITEM_HEIGHT}px`
      : defaultHeight;
  }

  public constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private i18nService: XVII18nService,
  ) {}

  public ngOnInit(): void {
    // Find the index of the currently selected option
    this.selectedIndex = findIndex(
      this.options,
      (option) => option.value === this.value,
    );

    this.offClickListener = this.renderer.listen(
      'document',
      'click',
      (event: MouseEvent) => {
        const targetElement = event.target as HTMLElement;

        if (!this.elementRef.nativeElement.contains(targetElement)) {
          this.isDropdownOpen = false;
        }
      },
    );
  }

  public ngOnDestroy(): void {
    if (isFunction(this.offClickListener)) {
      this.offClickListener();
    }
    this.offClickListener = null;
  }

  public selectOption(option: ISelectOptionsType): void {
    if (!this.isDisabled) {
      this.value = option.value as string;
      this.valueChange.emit(this.value);
      this.isDropdownOpen = false; // Close the dropdown after selecting an option
    }
  }

  public toggleDropdown(): void {
    if (!this.isDisabled) {
      this.isDropdownOpen = !this.isDropdownOpen;
      this.highlightedIndex = -1;
      if (this.isDropdownOpen) {
        this.searchText = '';
        this.filteredOptions = [...(this.options || [])];
        this.calculateDropdownPosition();
        this.scrollToSelected();
      }
    }
  }

  /**
   * Determines whether the dropdown should open upward.
   *
   * Strategy (matches Popper.js / Floating UI "flip" behaviour):
   * 1. Measure the trigger's position via getBoundingClientRect.
   * 2. Find the nearest scroll-clipping ancestor — for AG Grid cells this is
   *    `.ag-body-viewport`; otherwise fall back to the first
   *    `overflow: auto | scroll` ancestor; if none, use the window viewport.
   * 3. Flip to top when spaceBelow < DROPDOWN_MIN_HEIGHT AND spaceAbove > spaceBelow.
   *
   * The result is stored in `autoPositionTop` and OR-ed with the `isPositionTop`
   * input in the template so existing call-sites continue to work unchanged.
   */
  public calculateDropdownPosition(): void {
    const triggerRect: DOMRect =
      this.elementRef.nativeElement.getBoundingClientRect();
    const boundaryRect: DOMRect = this.getScrollBoundary();

    const spaceBelow: number = boundaryRect.bottom - triggerRect.bottom;
    const spaceAbove: number = triggerRect.top - boundaryRect.top;

    this.autoPositionTop =
      spaceBelow < XVICustomSelectComponent.DROPDOWN_MIN_HEIGHT &&
      spaceAbove > spaceBelow;
  }

  /**
   * Walks up the DOM to find the nearest element that clips overflow.
   * Prefers the AG Grid body viewport (`.ag-body-viewport`), then any ancestor
   * with `overflow-y: auto | scroll | hidden`, then falls back to the window.
   */
  private getScrollBoundary(): DOMRect {
    let firstOverflowEl: HTMLElement = null;
    let el: HTMLElement = this.elementRef.nativeElement.parentElement;

    while (el && el !== document.body) {
      // Prefer the AG Grid scrollable body — it is the real visual boundary
      if (el.classList.contains('ag-body-viewport')) {
        return el.getBoundingClientRect();
      }

      // Record the first overflow ancestor encountered, but keep walking up
      // in case ag-body-viewport appears higher in the tree.
      if (!firstOverflowEl) {
        const overflowY = window.getComputedStyle(el).overflowY;
        if (includes(['auto', 'scroll', 'hidden'], overflowY)) {
          firstOverflowEl = el;
        }
      }

      el = el.parentElement;
    }

    return firstOverflowEl
      ? firstOverflowEl.getBoundingClientRect()
      : new DOMRect(0, 0, window.innerWidth, window.innerHeight);
  }

  public getSelectedOptionText(): string {
    const selectedOption = find(
      this.options,
      (option) => option.value === this.value,
    );

    const defaultTipsValue = isNil(this.placeholder)
      ? I18n.Forms.GridForm.SelectAnOption
      : null;

    return this.i18nService.instant(
      selectedOption &&
        (isEmpty(this.emptyOption) ||
          !isEqual(selectedOption.value, this.emptyOption.value))
        ? selectedOption.text
        : defaultTipsValue,
    );
  }

  /**
   * Returns the list currently rendered in the dropdown.
   * When searchable and a search term is active, this is the filtered subset;
   * otherwise it is the full options list.
   * All keyboard navigation and selection logic must use this getter so that
   * arrow keys, ENTER, and ESC all operate on the same items the user sees.
   */
  public get visibleOptions(): ISelectOptionsType[] {
    return this.searchable ? this.filteredOptions : this.options || [];
  }

  public handleArrowKey(key: string): void {
    const maxIndex = this.visibleOptions.length - 1;

    if (key === KEY_CODES.ARROW_UP && this.highlightedIndex > 0) {
      this.highlightedIndex = this.highlightedIndex - 1;
    } else if (
      key === KEY_CODES.ARROW_DOWN &&
      this.highlightedIndex < maxIndex
    ) {
      this.highlightedIndex = this.highlightedIndex + 1;
    }
    this.scrollToSelected();
  }

  public scrollToSelected(): void {
    const containerElement: HTMLElement = this.selectBoxElement?.nativeElement;
    const highlightedElement = containerElement?.querySelectorAll(
      'li:not(.dropdown-search-item)',
    )[this.highlightedIndex] as HTMLElement;
    if (highlightedElement) {
      const containerRect = containerElement.getBoundingClientRect();
      const elementRect = highlightedElement.getBoundingClientRect();
      // Measure the sticky search bar's actual rendered height at runtime
      // instead of using a hardcoded constant, so scroll calculations remain
      // accurate regardless of font size, padding, or browser rounding.
      const searchBarEl = containerElement.querySelector(
        '.dropdown-search-item',
      ) as HTMLElement | null;
      const stickyOffset = searchBarEl?.offsetHeight ?? 0;

      const highlightedTop =
        elementRect.top - containerRect.top + containerElement.scrollTop;
      const highlightedBottom =
        highlightedTop + highlightedElement.clientHeight;
      const containerBottom =
        containerElement.scrollTop + containerElement.clientHeight;

      if (highlightedTop < containerElement.scrollTop + stickyOffset) {
        containerElement.scrollTop = highlightedTop - stickyOffset;
      } else if (highlightedBottom > containerBottom) {
        containerElement.scrollTop =
          highlightedBottom - containerElement.clientHeight;
      }
    }
  }

  public onKeydown(event: KeyboardEvent): void {
    // eslint-disable-next-line import/no-deprecated
    const keyCode: number = event.keyCode;
    const isNavigationKey: boolean = includes(
      values(NAVIGATION_KEY_CODES),
      keyCode,
    );

    const clickTabWithoutDropdownOpen =
      !this.isDropdownOpen && isEqual(NAVIGATION_KEY_CODES.KEY_TAB, keyCode);

    if (isNavigationKey && !clickTabWithoutDropdownOpen) {
      event.stopPropagation();
    }

    if (
      includes(
        [
          KEY_CODES.TAB,
          KEY_CODES.SHIFT,
          KEY_CODES.ARROW_UP,
          KEY_CODES.ARROW_DOWN,
          KEY_CODES.ARROW_LEFT,
          KEY_CODES.ARROW_RIGHT,
        ],
        event.key,
      )
    ) {
      event.preventDefault();
    }

    if (this.isDropdownOpen) {
      if (
        event.key === KEY_CODES.ARROW_UP ||
        event.key === KEY_CODES.ARROW_DOWN
      ) {
        this.handleArrowKey(event.key);
      } else if (event.key === KEY_CODES.ENTER) {
        event.preventDefault();

        this.selectHighlightedOption();
      }
    }

    if (event.key === KEY_CODES.ESCAPE && this.selectedIndex > -1) {
      // When searchable, restore by value match against full options list
      // because selectedIndex was set against the full list in ngOnInit.
      const option = this.searchable
        ? find(this.options, (o) => o.value === this.value)
        : this.options[this.selectedIndex];
      if (option) {
        this.selectOption(option);
      }
    }
  }

  public onSearchInputKeydown(event: KeyboardEvent): void {
    if (
      event.key === KEY_CODES.ARROW_UP ||
      event.key === KEY_CODES.ARROW_DOWN ||
      event.key === KEY_CODES.ENTER ||
      event.key === KEY_CODES.ESCAPE
    ) {
      event.preventDefault();
      this.onKeydown(event);
    } else {
      event.stopPropagation();
    }
  }

  public onSearchChange(): void {
    const keyword = this.searchText.toLowerCase().trim();
    this.filteredOptions = keyword
      ? (this.options || []).filter((o) =>
          o.text.toLowerCase().includes(keyword),
        )
      : [...(this.options || [])];
    this.highlightedIndex = -1;
  }

  public selectHighlightedOption(): void {
    if (
      this.highlightedIndex >= 0 &&
      this.highlightedIndex < this.visibleOptions.length
    ) {
      this.selectOption(this.visibleOptions[this.highlightedIndex]);
    }
  }

  public onGridCustomFocus(event): void {
    this.toggleDropdown();
    (event.target as HTMLInputElement).focus();
  }

  public onFocusIn(event: Event): void {
    event.preventDefault();

    // need this to clear default action of tab (will select input content)
    (event.target as HTMLInputElement).setSelectionRange(0, 0);

    this.focusin.emit(event);
  }
}
